import type {
  ChecklistCategory,
  ChecklistField,
  ChecklistFieldType,
  ChecklistSchema,
  ChecklistSection,
} from "@/app/_services/ChecklistService";
import { DEFAULT_CHECKLIST_ICON } from "@/app/admin/_common/checklistIcons";

// `locked` items came from the server: their key is frozen because shows store answers by it.
export type DraftField = Omit<ChecklistField, "options"> & {
  uid: string;
  locked: boolean;
  options: string[];
};

export type DraftSection = Omit<ChecklistSection, "fields"> & {
  uid: string;
  locked: boolean;
  fields: DraftField[];
};

export type DraftCategory = Omit<ChecklistCategory, "sections"> & {
  uid: string;
  locked: boolean;
  sections: DraftSection[];
};

const uid = () => crypto.randomUUID();

export function slugify(label: string, fallback: string) {
  const slug = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return slug || fallback;
}

export function uniqueKey(base: string, taken: string[]) {
  if (!taken.includes(base)) return base;
  let n = 2;
  while (taken.includes(`${base}_${n}`)) n += 1;
  return `${base}_${n}`;
}

export function fromSchema(schema: ChecklistSchema): DraftCategory[] {
  return schema.categories.map((category) => ({
    ...category,
    uid: uid(),
    locked: true,
    sections: category.sections.map((section) => ({
      ...section,
      uid: uid(),
      locked: true,
      fields: section.fields.map((field) => ({
        ...field,
        help: field.help ?? "",
        uid: uid(),
        locked: true,
        options: field.options ?? [],
      })),
    })),
  }));
}

export function toSchema(categories: DraftCategory[]): ChecklistSchema {
  return {
    version: 2,
    categories: categories.map(({ key, label, icon, sections }) => ({
      key,
      label,
      icon,
      sections: sections.map(({ key, label, fields }) => ({
        key,
        label,
        fields: fields.map(({ key, label, type, required, help, options }) => ({
          key,
          label,
          type,
          required,
          help,
          ...(type === "select" ? { options } : {}),
        })),
      })),
    })),
  };
}

/** Section keys are unique across the template, so collect them from every category. */
export function allSectionKeys(categories: DraftCategory[], exceptUid?: string) {
  return categories.flatMap((c) => c.sections).filter((s) => s.uid !== exceptUid).map((s) => s.key);
}

export function newCategory(taken: string[]): DraftCategory {
  return {
    uid: uid(),
    locked: false,
    key: uniqueKey("category", taken),
    label: "",
    icon: DEFAULT_CHECKLIST_ICON,
    sections: [],
  };
}

export function newSection(taken: string[]): DraftSection {
  return {
    uid: uid(),
    locked: false,
    key: uniqueKey("section", taken),
    label: "",
    fields: [],
  };
}

export function newField(taken: string[], type: ChecklistFieldType = "checkbox"): DraftField {
  return {
    uid: uid(),
    locked: false,
    key: uniqueKey("field", taken),
    label: "",
    type,
    required: false,
    help: "",
    options: [],
  };
}

export function move<T>(items: T[], index: number, delta: number): T[] {
  const target = index + delta;
  if (target < 0 || target >= items.length) return items;
  const next = [...items];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

export function moveTo<T>(items: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) return items;
  const next = [...items];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}
